/**
 * The quiet-path gate's fault sentences. Each names the band where the route is cut - the band
 * the culprit stands in, because that is where the generator has something to change - how
 * near the goal a crawling ranger gets, and who stops him.
 */

import type { Rect } from '../../../sim/types';
import type { Band } from '../../types';
import type { Mover, Watcher } from './model';

/** How far beyond the most advanced stretch of watched ground the blame still looks, metres. */
const BLAME_DEPTH = 10;

export function bandName(bands: readonly Band[], z: number): string {
  const i = bands.findIndex((b) => z >= b.z0 && z < b.z1);
  const kind = bands[i]?.kind;
  if (kind === 'approach') return `approach band ${i}`;
  if (kind === 'drop') return 'the drop band';
  return kind === 'belt' ? 'the belt' : kind === 'goal' ? 'the goal band' : 'the map';
}

function shortOf(rect: Rect, cell: number, width: number): number {
  const x = (cell % width) + 0.5;
  const z = Math.floor(cell / width) + 0.5;
  const dx = Math.max(rect.x0 - x, 0, x - rect.x1);
  const dz = Math.max(rect.z0 - z, 0, z - rect.z1);
  return Math.sqrt(dx * dx + dz * dz);
}

export interface Stop {
  x: number;
  z: number;
  /** Metres short of the goal. */
  short: number;
}

/** The reached cell nearest the goal. */
export function closest(reached: Uint8Array, width: number, rect: Rect): Stop {
  let best: Stop = { x: 0, z: 0, short: Infinity };
  for (let cell = 0; cell < reached.length; cell++) {
    if (reached[cell] === 0) continue;
    const short = shortOf(rect, cell, width);
    if (short < best.short) best = { x: cell % width, z: Math.floor(cell / width), short };
  }
  return { ...best, short: Math.round(best.short) };
}

/**
 * Shut by men who never move. The culprit is whoever watches most of the ground he would have to cross
 * next - of the watched cells that touch ground he can reach, only those nearest the goal count.
 */
export function staticFault(
  bands: readonly Band[], rect: Rect, width: number, reached: Uint8Array, seenBy: Int16Array,
  watchers: readonly Watcher[],
): { fault: string; stop: Stop } {
  const edge: number[] = [];
  let nearest = Infinity;
  for (let cell = 0; cell < seenBy.length; cell++) {
    if ((seenBy[cell] ?? -1) < 0) continue;
    const x = cell % width;
    const beside = [x > 0 ? cell - 1 : -1, x < width - 1 ? cell + 1 : -1, cell - width, cell + width];
    if (!beside.some((n) => n >= 0 && n < reached.length && reached[n] !== 0)) continue;
    edge.push(cell);
    nearest = Math.min(nearest, shortOf(rect, cell, width));
  }
  const tally = new Map<number, number>();
  for (const cell of edge) {
    if (shortOf(rect, cell, width) > nearest + BLAME_DEPTH) continue;
    const who = seenBy[cell] ?? -1;
    tally.set(who, (tally.get(who) ?? 0) + 1);
  }
  const worst = [...tally.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
  const culprit = worst ? watchers[worst[0]] : undefined;
  const stop = closest(reached, width, rect);
  const fault = `the quiet route is cut in ${bandName(bands, culprit?.z ?? stop.z)}: a crawling ranger gets no nearer `
    + `the goal than ${stop.short} m (at ${stop.x}, ${stop.z}) without crossing ground watched by `
    + `${culprit?.label ?? 'nobody: the ground itself is shut'}`;
  return { fault, stop };
}

/** Open to a man who never has to wait - but these cannot be waited out. Blames the mover that passes nearest. */
export function timedFault(
  bands: readonly Band[], rect: Rect, width: number, reached: Uint8Array, movers: readonly Mover[],
): { fault: string; stop: Stop } {
  const stop = closest(reached, width, rect);
  let culprit: Mover | undefined;
  let at = stop.z;
  let best = Infinity;
  for (const m of movers) {
    for (const look of m.looks) {
      const d = (look.x - stop.x) * (look.x - stop.x) + (look.z - stop.z) * (look.z - stop.z);
      if (d >= best) continue;
      best = d;
      culprit = m;
      at = look.z;
    }
  }
  const fault = `the quiet route is cut in ${bandName(bands, at)}: from ${stop.x}, ${stop.z}, ${stop.short} m short of `
    + `the goal, a crawling ranger finds no gap between the passes of ${culprit?.label ?? 'the patrols'}`;
  return { fault, stop };
}
